import {Component, computed, effect, inject, input, OnInit} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatButton} from '@angular/material/button'
import {MatCheckbox} from '@angular/material/checkbox'
import {Applicant, Child, Income, Loan, Property} from '@sparbanken-syd/kalpylator'
import {Subject} from 'rxjs'
import {environment} from '../../../environments/environment'
import {BorgoService} from '../../services/borgo.service'
import {KalpService} from '../../services/kalp.service'
import {ConfigService} from '@sparbanken-syd/sparbanken-syd-bankid'
import {getApplicant, getChild, getIncome} from '../../services/types'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'


@Component({
  selector: 'spb-test',
  templateUrl: './test.component.html',
  styleUrls: ['./test.component.scss'],
  imports: [
    MatCheckbox,
    ReactiveFormsModule,
    MatButton
  ]
})
export class TestComponent implements OnInit {


  readonly type = input<'APPLICANTS' | 'BORGO' | 'INCOMES' | 'LOANS' | 'PROPERTIES' | 'APPLICATION'>('BORGO')

  public borgo: FormControl<boolean> = new FormControl<boolean>(true, {nonNullable: true})

  public otherBuildings: FormControl<boolean> = new FormControl<boolean>(false, {nonNullable: true})

  public coApplicant: FormControl<boolean> = new FormControl<boolean>(true, {nonNullable: true})

  /**
   * Test functions available?
   */
  public testMode: boolean = environment.testMode

  /**
   * Use this to stop subscribing to stuff
   *
   * @private
   */
  private readonly stop$ = new Subject<boolean>()
  private readonly borgoService = inject(BorgoService)
  private readonly kalpService = inject(KalpService)
  private readonly configService = inject(ConfigService)
  private readonly mainSub = computed(() => this.configService.currentUser()?.sub)
  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  constructor() {
    effect(() => {
      this.borgo.setValue(this.borgoService.borgoPossible())
    })
  }

  public ngOnInit(): void {


    this.borgo.valueChanges
      .pipe(this.takeUntilDestroyed())
      .subscribe((value: boolean) => {
        this.borgoService.borgoPossible.set(value)
      })

    this.kalpService.applicantCount$
      .pipe(this.takeUntilDestroyed())
      .subscribe((count: number) => this.coApplicant.setValue(count > 1))
  }

  public setTest(): void {
    switch (this.type()) {
    case 'BORGO':
      this.setPreconditions(this.borgo.value)
      break
    case 'APPLICANTS':
      this.setApplicants()
      break
    case 'INCOMES':
      this.setIncomes()
      break
    case 'LOANS':
      this.setLoans()
      break
    case 'PROPERTIES':
      this.setProperties(this.otherBuildings.value)
      break
    case 'APPLICATION':
      this.fillAll()
    }
  }

  public fillAll(): void {
    this.setPreconditions(this.borgo.value)
    this.setProperties(this.otherBuildings.value)
    this.setApplicants()
    this.setLoans()
    this.setIncomes()
  }

  private setPreconditions(borgo: boolean): void {
    this.borgoService.borgoPossible.set(borgo)
    this.borgo.setValue(borgo, {emitEvent: true})
  }

  private setApplicants(): void {
    const applicants: Applicant[] = [getApplicant(), getApplicant()]
    applicants[0].occupationType = 'EMPLOYED'
    applicants[0].sub = this.mainSub()
    applicants[0].name = 'Anders Olsson'

    applicants[1].occupationType = 'EMPLOYED'
    applicants[1].sub = '1959005123259'
    applicants[1].name = 'Johanna Gustavsson'

    if (!this.coApplicant.value) {
      applicants.splice(1, 1)
    }
    this.kalpService.applicants$.next(applicants)

    const children: Child[] = [getChild(), getChild()]
    children[0].age = 4
    children[1].age = 15
    this.kalpService.children$.next(children)
  }

  private setIncomes(): void {
    const incomes: Income[] = [getIncome(), getIncome()]

    Object.assign(incomes[0], {
      monthlyIncome: 28000, taxFreeIncome: 1000, studyLoan: 1250
    })
    Object.assign(incomes[1], {
      monthlyIncome: 16500, studyLoan: 1250, otherCosts: 800
    })
    if (!this.coApplicant.value) {
      incomes.splice(1, 1)
    }
    this.kalpService.incomes$.next(incomes)
  }

  private setLoans(): void {
    const loans: Loan[] = [
      {
        amount: 25000,
        loanType: 'BLANCO',
        id: '1',
        type: 'loans',
        mortgage: 0,
        interestRate: 0.082
      },
      {
        amount: 250000,
        loanType: 'BORGEN',
        id: '2',
        type: 'loans',
        mortgage: 1000,
        interestRate: 0.082
      },
      {
        amount: 1.2 * 1000 * 1000,
        loanType: 'MORTGAGE',
        id: '1',
        type: 'loans',
        mortgage: 10,
        interestRate: 0.0225,
        keep: true,
        new: true
      }
    ]
    this.kalpService.loans$.next(loans)
  }

  private setProperties(otherBuildings: boolean): void {
    const properties: Property[] = [
      {
        type: 'properties',
        id: '1',
        propertyType: 'VILLA',
        primary: true,
        loanAmount: 3000 * 1000 * 0.85,
        marketValue: 3000 * 1000
      },
      {
        type: 'properties',
        id: '2',
        propertyType: 'BOSTADSRATT',
        primary: false,
        keep: false,
        rent: 4000,
        loanAmount: 850 * 1000 * 0.85,
        marketValue: 850 * 1000
      },
      {
        type: 'properties',
        id: '3',
        propertyType: 'FRITIDSHUS',
        primary: false,
        keep: false,
        loanAmount: 250 * 1000 * 0.85,
        marketValue: 250 * 1000
      }
    ]

    if (!otherBuildings) {
      properties.pop()
    }
    this.kalpService.properties$.next(properties)
  }
}
